import React, { useEffect, useRef, useState } from "react";
import "./App.css";

const FractalPage = () => {
  const canvasRef = useRef(null);
  const [step, setStep] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const maxSteps = 9; // Increased to 9 steps

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas dimensions
    const updateCanvasSize = () => {
      const size = Math.min(window.innerWidth * 0.7, window.innerHeight * 0.7);
      canvas.width = size;
      canvas.height = size;
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);

    // Animation
    let animationId;
    let currentStep = step;
    const stepDuration = 800; // Duration for each step in ms
    const startTime = performance.now();

    const animate = (timestamp) => {
      const elapsed = timestamp - startTime;
      const elapsedSteps = Math.min(
        Math.floor(elapsed / stepDuration),
        maxSteps - currentStep
      );

      if (elapsedSteps > 0) {
        currentStep += elapsedSteps;
        setStep(currentStep);
      }

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw fractal
      ctx.fillStyle = "#000000"; // Pure black background
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the fractal pattern
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const maxSize = Math.min(canvas.width, canvas.height) * 0.25; // Increased size from 0.15 to 0.25

      ctx.strokeStyle = "#FFFFFF"; // Pure white lines
      ctx.lineWidth = 1.0; // Thinner lines (was 2.5)

      // Draw just a single cross pattern at the center
      drawCrossPattern(ctx, centerX, centerY, maxSize, currentStep, null);

      if (currentStep < maxSteps) {
        animationId = requestAnimationFrame(animate);
      } else {
        setAnimationComplete(true);
      }
    };

    // Direction values: 0=top, 1=right, 2=bottom, 3=left
    const drawCrossPattern = (ctx, x, y, size, depth, prevDirection) => {
      if (depth <= 0 || size < 1) return;

      // Draw main plus sign
      ctx.beginPath();
      ctx.moveTo(x, y - size); // Vertical line top
      ctx.lineTo(x, y + size); // Vertical line bottom
      ctx.moveTo(x - size, y); // Horizontal line left
      ctx.lineTo(x + size, y); // Horizontal line right
      ctx.stroke();

      if (depth > 1) {
        // Draw smaller crosses at the tips, but not in the same direction as before
        const newSize = size / 2; // Changed from 1/3 to 1/2 so each new line is exactly half size

        // Top (0) - don't go top again if we just came from bottom
        if (prevDirection !== 0) {
          drawCrossPattern(ctx, x, y - size, newSize, depth - 1, 2);
        }

        // Right (1) - don't go right again if we just came from left
        if (prevDirection !== 1) {
          drawCrossPattern(ctx, x + size, y, newSize, depth - 1, 3);
        }

        // Bottom (2) - don't go bottom again if we just came from top
        if (prevDirection !== 2) {
          drawCrossPattern(ctx, x, y + size, newSize, depth - 1, 0);
        }

        // Left (3) - don't go left again if we just came from right
        if (prevDirection !== 3) {
          drawCrossPattern(ctx, x - size, y, newSize, depth - 1, 1);
        }
      }
    };

    // Start animation
    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", updateCanvasSize);
      cancelAnimationFrame(animationId);
    };
  }, [step]);

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    textAlign: "center",
    color: "#FFFFFF",
  };

  const canvasStyle = {
    maxWidth: "100%",
    maxHeight: "70vh",
    margin: "20px auto",
    display: "block",
  };

  return (
    <div className="container">
      <h1>Fractal Generator</h1>
      <canvas ref={canvasRef} style={canvasStyle} />
      <div style={{ marginTop: "20px" }}>
        <div style={pageStyle}>
          <p>
            Step: {step} of {maxSteps}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FractalPage;
